const EN_APP_COPY = {
  nav: {
    overview: "Overview",
    journal: "Journal",
    features: "Features",
    familyChart: "Family chart",
    media: "Media",
    verification: "Verification",
    photoGallery: "Photo gallery",
    myAccount: "My account",
  },
  accountMenu: {
    operationsConsole: "Operations console",
    myProfile: "My profile",
    paymentHistory: "Payment history",
    changePassword: "Change password",
    signOut: "Sign out",
  },
  authHero: {
    ariaLabel: "What AfterMe helps with",
    kicker: "What you can set up",
    title: "Keep the first steps clear and private.",
    points: [
      "Save journals, guidance, and private memories in one place.",
      "Build your family tree before anyone needs to guess who gets what.",
      "Choose trusted contacts and release paths with calm, simple controls.",
    ],
  },
  featureCards: {
    privateJournals: {
      title: "Private journals",
      description:
        "Write long-form entries with formatting, tag who may receive them later, and keep drafts private until release.",
      open: "Journal open below — click again to close",
      closed: "Click to open journal →",
    },
    familyConfessions: {
      title: "Family confessions",
      description:
        "Record messages or confessions meant for specific relatives—released only through your chosen verification path.",
    },
    photoMemoryCollage: {
      title: "Photo and memory collage",
      description:
        "Upload photos and short videos, tag family members, and curate a gallery that grows with your story.",
      open: "Gallery open below — click again to close",
      closed: "Click to open media gallery →",
    },
    posthumousReleaseWorkflow: {
      title: "Posthumous release workflow",
      description:
        "See how journals and media move from locked to released after the checks you define with family and advisors.",
    },
    active: "Active",
    previewOpen: "Panel open below — click again to close",
    previewClosed: "Click to preview →",
  },
  dashboard: {
    welcome: (name) => `Welcome, ${name}`,
    openMediaGallery: "Open media gallery",
    placeholderTitle: "Feature workspace",
    placeholderBody:
      "Structured workflow with tagged recipients, posthumous controls, and traceable release logs.",
    placeholderHelperPrefix: "This workspace will host dedicated tools for this area. For now, use",
    placeholderHelperStrong: "Private journals",
    placeholderHelperSuffix: "to write and save entries.",
    selectHint:
      "Select a card above to open its workspace. Click the same card again to close it and shorten the page.",
  },
  profileHero: {
    eyebrow: "My account",
    title: "My profile",
    body:
      "Your personal details, address, occupation, and optional health context are saved to your AfterMe profile so you can keep everything current in one place.",
    back: "Back to dashboard",
    asideAria: "Profile setup overview",
    asideKicker: "Profile setup",
    asideTitle: "Guided profile sections",
    asideBody:
      "Move through personal details, contact info, identity context, health notes, and release settings without one long scroll.",
  },
  paymentHero: {
    eyebrow: "My account",
    title: "Payment history",
    body:
      "Review recent charges and subscription payments recorded for your account. For profile details, use My profile in the same menu.",
    back: "Back to dashboard",
    asideAria: "Billing overview",
    asideKicker: "Billing",
    asideTitle: "Read-only ledger",
    asideBody:
      "Entries appear here when your payment provider or internal jobs write billing events to your account.",
  },
  adminHero: {
    eyebrow: "Owner operations",
    title: "Users, leads, and internal follow-up.",
    body:
      "This is a protected operations area for your team. It uses the same sign-in session, but keeps back-office work separate from the customer-facing experience.",
    back: "Back to dashboard",
    asideAria: "Operations summary",
    asideKicker: "Today's operations",
    asideTitle: "One internal surface for product follow-up.",
    asidePoints: [
      "Review registered users and account signals",
      "Track new contact requests and outreach notes",
      "Prepare for future verification queues in the same console",
    ],
  },
  familyHero: {
    eyebrow: "Trusted people",
    title: "Your trusted people",
    body:
      "Add the people who matter in your plan. You can decide later who sees what and when.",
    bannerStrong: "Nothing is shared automatically.",
    bannerTail: "You stay in control.",
    back: "Back to dashboard",
    asideAria: "Family chart overview",
    asideKicker: "Trusted setup",
    asideTitle: "Start with the people closest to your future plan.",
    asidePoints: [
      "Add a partner, child, parent, or sibling first",
      "Capture contact details only when you want future access involved",
      "Preview the structure before release workflows ever matter",
    ],
  },
  familyChart: {
    label: "Your family structure preview",
    title: "See how your trusted people connect",
    lead:
      "Tap a person to focus their details, hover to follow their connections, and drag cards to tidy the view without changing the family order.",
    summaryAria: "Family map tips",
    summaryParents: "Parents above, children below",
    summaryTap: "Tap a card for details",
    summaryDrag: "Drag to tidy the view",
    svgAria: "Family access chart",
    focusedEyebrow: "Focused member",
    ownerFocus:
      "You anchor the plan. Parents sit above, your generation stays beside you, and younger generations move downward as your sharing plan expands.",
    memberFocus:
      "{name} is shown in the {relation} part of your plan so you can review how they fit before sharing anything.",
    accountHolder: "Account holder",
    alreadyUsing: "Already using AfterMe",
    inviteRequested: "Invite requested",
    accessReady: "Access ready",
    noEmailYet: "No email yet",
    centerOfPlan: "Center of your plan",
    aboveYou: "Above you in the family line",
    sameGeneration: "Same generation as you",
    belowYou: "Below you in the family line",
    belowParent: (name) => `Below ${name}`,
    besideYou: "Shown beside you",
    besidePartner: "Shown beside their partner",
    branchUnder: (name) => `Branch under ${name}`,
    familyMember: "Family member in your plan",
    otherParent: (name) => `Other parent: ${name}`,
  },
  mediaHero: {
    kicker: "Memory vault",
    title: "Your memories, safely preserved",
    lead:
      "Store your photos, documents, and important moments in a secure space. You decide who sees them and when.",
    badgesAria: "Memory vault trust highlights",
    badgeEncrypted: "🔒 Encrypted & private",
    badgeRelease: "⏳ Release controls available",
    badgeTrusted: "👨‍👩‍👧 Share with trusted family",
    addMemory: "Add memory",
    openGallery: "Open gallery",
    back: "Back to dashboard",
    loading: "Loading vault details…",
    usageKicker: "Storage confidence",
    usagePlenty: "You have plenty of space for your memories.",
    usageWarning:
      "Your vault is nearly full. Clear older items or upgrade when billing is available.",
    usageReview:
      "You are using a larger share of your space now. Review what stays in your vault and what can be removed.",
    viewStorageDetails: "View storage details",
    memories: "Memories",
    spaceUsed: "Space used",
    planSpace: "Your plan space",
    spaceLeft: "Space left",
  },
  verificationHero: {
    eyebrow: "Release & trust",
    title: "Post-death verification",
    body:
      "Review how confessions and private journals can be released only after approved checks. Options here describe your intended workflow; final activation is handled when your account moves into release.",
    back: "Back to dashboard",
    asideAria: "Verification workflow summary",
    asideKicker: "Workflow",
    asideTitle: "Quiet checks before anything sensitive is released.",
    asidePoints: [
      "Choose the proof path you want trusted people to follow",
      "Set the release tone before journals or confessions ever move",
      "Keep approval steps separate from everyday account access",
    ],
  },
  verificationWorkflow: {
    heading: "Post-Death Verification Workflow",
    lead: "Confession and private journals are released only after approved checks.",
    offlineStatus: "Offline validation status",
    manualReviewRequired: "Manual review required",
  },
  verificationOptions: [
    {
      id: "identity",
      title: "National Identity Validation",
      description:
        "Authorized nominee submits identity information and a signed confirmation for offline validation.",
    },
    {
      id: "family-proof",
      title: "Family Proof Submission",
      description:
        "Tagged family member submits death certificate and relationship proof for manual approval.",
    },
    {
      id: "inactive-check",
      title: "Inactivity + Offline Check",
      description:
        "Account inactivity triggers a timed workflow, then support validates evidence before release.",
    },
  ],
  paymentHistory: {
    title: "Payment history",
    body:
      "Recent charges and subscription payments for your account. This list updates when billing events are recorded.",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    loading: "Loading payment history…",
    fallbackPayment: "Payment",
    emptyState:
      "No payments recorded yet. When you subscribe or are charged, entries will show up here.",
    notYet: "Not yet",
    loadError: "Could not load payment history.",
  },
  modals: {
    changePassword: {
      title: "Change password",
      lead: "Enter your current password and a new password (at least 8 characters).",
      currentPassword: "Current password",
      newPassword: "New password",
      confirmNewPassword: "Confirm new password",
      fillAllFields: "Fill in all fields.",
      minLength: "New password must be at least 8 characters.",
      mismatch: "New password and confirmation do not match.",
      updateFailed: "Could not update password.",
      updated: "Password updated.",
      saving: "Saving…",
      save: "Save new password",
      cancel: "Cancel",
    },
    sessionEnding: {
      title: "Session ending soon",
      leadPrefix:
        "For security, your sign-in session lasts 15 minutes. When it ends you are signed out automatically.",
      timeRemaining: "Time remaining: about",
      oneMinute: "1 minute",
      manyMinutes: (count) => `${count} minutes`,
      secondLine: "Extend your session to keep working, or sign out now.",
      extending: "Extending…",
      extend: "Extend session",
      signOut: "Sign out",
    },
    familyDelete: {
      title: "Remove family member?",
      lead: (name) =>
        `Remove ${name} from your chart. Any media tagged for this person will have that tag cleared, and media folders that used them as the saved default recipient will have that default removed.`,
      removing: "Removing…",
      remove: "Remove member",
      cancel: "Cancel",
    },
  },
  heroRotator: {
    regionLabel: "Product highlights",
    dotsLabel: "Highlight slides",
    slideLabel: (index, label) => `Show slide ${index}: ${label}`,
    slides: [
      {
        key: "journals",
        label: "Letters & journals",
        title: "Leave words your family can return to",
        body:
          "Write the letters, context, and practical notes your loved ones may need when emotions are high and answers are scarce.",
        variant: "mint",
      },
      {
        key: "media",
        label: "Photos & memories",
        title: "Keep precious memories private until release",
        body:
          "Store photos and short clips with recipient tags so meaningful moments reach the right people at the right time.",
        variant: "ocean",
      },
      {
        key: "family",
        label: "Family access",
        title: "Map who should receive what",
        body:
          "Build a family access path that makes your intentions visible before anyone has to interpret them later.",
        variant: "violet",
      },
      {
        key: "trust",
        label: "Built for trust",
        title: "Release designed around checks and dignity",
        body:
          "Structured workflows, review-minded release steps, and a calm Canadian product voice for a deeply sensitive moment.",
        variant: "ember",
      },
    ],
  },
  trustedPeople: {
    relationshipOptions: [
      "Parent",
      "Spouse",
      "Partner",
      "Ex-spouse",
      "Sibling",
      "Child",
      "Grandchild",
      "Friend",
      "Legal Advisor",
    ],
    ownerRelation: "Owner",
    statusYou: "You",
    statusConnected: "Connected",
    statusReadyToConnect: "Ready to connect",
    statusInviteSent: "Invite sent",
    statusInviteCancelled: "Invite cancelled",
    statusNotInvited: "Not invited",
    badgePlanOwner: "Plan owner",
    badgeNoAccessYet: "No access yet",
    badgePrimaryTrusted: "Primary trusted",
    badgeFamily: "Family",
    supportOwner: "You stay in control of who receives anything later.",
    supportConnected: "Connected and ready for future access choices.",
    supportExistingUser:
      "They already have an AfterMe account. Send an invite so both family charts can connect.",
    supportInviteRequested:
      "Invite is waiting for their acceptance. You can re-send it if they still need a reminder later.",
    supportInviteCancelled:
      "Invitation was cancelled. You can send a fresh invite again whenever you feel ready.",
    supportEmailReady: "Email is ready if you choose to involve them later.",
    supportNoAccess: "Added to your plan, but not set up for access yet.",
    relationshipHints: {
      Parent: "Shown above you in the family structure.",
      Spouse: "Shown beside you or beside the partner you choose.",
      Partner: "Shown beside you or beside the partner you choose.",
      Child: "Shown below you in the family line.",
      Grandchild: "Placed below one of your children.",
      Sibling: "Shown alongside your generation.",
      default: "You can adjust access choices later.",
    },
    updatedPrefix: "Updated",
    addedRecently: "Added recently",
    firstSent: "First sent",
    lastSent: "Last sent",
    lastResent: "Last re-sent",
    resendAvailable: "Re-send available",
    resendRule: "Invites can be re-sent every 3 days.",
    noEmailYet: "No email yet",
    lastInviteSent: "Last invite sent",
    inviteResendAvailable: "Invite re-send available",
    invitationCancelled: "Invitation cancelled",
    saving: "Saving...",
    saveContact: "Save contact",
    edit: "Edit",
    cancel: "Cancel",
    sending: "Sending...",
    sendInvite: "Send invite",
    sendInviteAgain: "Send invite again",
    resending: "Re-sending...",
    resendInvite: "Re-send invite",
    cancelling: "Cancelling...",
    cancelInvitation: "Cancel invitation",
    remove: "Remove",
    pendingInvitationEyebrow: "Pending family invitation",
    pendingInvitationBody:
      "{ownerName} added you as {inviteeName} in their family plan. Accept to connect both accounts in the family chart.",
    sentPrefix: "Sent",
    acceptInvitation: "Accept invitation",
    connecting: "Connecting...",
    guidanceAria: "How to get started",
    guidanceKicker: "Start simple",
    guidanceTitle: "Add the first people you trust most.",
    guidanceBody:
      "Add your partner, parent, child, or another close person now. You can always come back to expand the family structure when the rest of your plan is ready.",
    guidancePoints: [
      "Add your partner or closest family member first",
      "Set future access later when you feel ready",
      "This helps you decide who receives what down the road",
    ],
    panelTitle: "Build your trusted people circle",
    panelBody:
      "Add the people who matter in your plan. You can decide later who sees what and when.",
    panelBannerStrong: "Nothing is shared automatically.",
    panelBannerTail: "You stay in control.",
    invitationsHead: "Invitations waiting for you",
    invitationsBody: "Accept a connection to appear in one another’s family chart.",
    stepName: "1. Name",
    stepRelationship: "2. Relationship",
    stepAccess: "3. Access",
    stepDetails: "4. More details",
    step1: "Step 1",
    step1Title: "Who should be in your plan?",
    name: "Name",
    fullName: "Full name",
    next: "Next",
    continueLater: "Continue later",
    step2: "Step 2",
    step2Title: "How do they fit into your family?",
    relationship: "Relationship",
    withParent: "With parent",
    optional: "(optional)",
    chooseSomeone: "Choose someone already on your list",
    branchUnder: "Branch under",
    chooseChildBranch: "Choose which child branch they belong to",
    addChildFirst: "Add a child first",
    showBeside: "Show beside",
    besideYou: "You (account holder)",
    back: "Back",
    step3: "Step 3",
    step3Title: "How might they be involved later?",
    mayReceiveFuture: "This person may receive something in the future",
    email: "Email",
    emailPlaceholder: "Add the email they use or will use for AfterMe",
    emailHelp:
      "Email is required so we can connect both sides of the family chart when they accept your invitation later.",
    hideDetails: "Hide details",
    addMoreDetails: "Add more details",
    optionalSection: "Optional",
    optionalTitle: "Add more details",
    birthYear: "Birth year",
    birthYearPlaceholder: (year) => `e.g. ${year}`,
    nickname: "Nickname",
    nicknamePlaceholder: "What you call them",
    pronouns: "Pronouns",
    pronounsPlaceholder: "Choose if you like",
    pronounsOptions: ["He / him", "She / her", "They / them", "Uses their name only", "Other"],
    personalContext: "Personal context",
    personalContextPlaceholder: "Anything you want remembered respectfully",
    notes: "Notes",
    notesPlaceholder:
      "Anything that matters for your plan, family context, or future conversations.",
    addPerson: "Add person",
    trialUnlockEyebrow: "Trial unlock",
    trialUnlockFallback: "Start trial to add more trusted people",
    startTrial: "Start trial",
    seePlans: "See plans",
    sidebarAria: "Trusted people already in your plan",
    sidebarTitle: "People already in your plan",
    sidebarBody: "Keep this light now. You can grow the circle whenever you are ready.",
    emptyTitle: "Add your first trusted person",
    emptyBody:
      "Start with the partner, parent, child, or sibling you are most likely to plan around first.",
    addSomeone: "Add someone",
    privacyAria: "Privacy and control",
    privacyKicker: "Your privacy matters",
    privacyTitle: "You stay in control at every step.",
    privacyPoints: [
      "Only you decide who receives access later.",
      "Nothing is shared automatically just because someone is listed here.",
      "You can change relationships, emails, and roles anytime.",
    ],
  },
  familyInvitationNotice: {
    waitingOne: "You have 1 family invitation waiting in Family chart.",
    waitingMany: (count) => `You have ${count} family invitations waiting in Family chart.`,
    connectedWith: (ownerName) =>
      `You are now connected with ${ownerName || "your family contact"} in Family chart.`,
  },
};

const FR_APP_COPY = {
  nav: {
    overview: "Aperçu",
    journal: "Journal",
    features: "Fonctionnalités",
    familyChart: "Arbre familial",
    media: "Médias",
    verification: "Vérification",
    photoGallery: "Galerie photo",
    myAccount: "Mon compte",
  },
  accountMenu: {
    operationsConsole: "Console d'opérations",
    myProfile: "Mon profil",
    paymentHistory: "Historique des paiements",
    changePassword: "Changer le mot de passe",
    signOut: "Se déconnecter",
  },
  authHero: {
    ariaLabel: "Ce qu'AfterMe vous aide à mettre en place",
    kicker: "Ce que vous pouvez préparer",
    title: "Gardez les premières étapes claires et privées.",
    points: [
      "Conservez journaux, consignes et souvenirs privés au même endroit.",
      "Construisez votre arbre familial avant que quelqu'un doive deviner qui reçoit quoi.",
      "Choisissez des contacts de confiance et des parcours de diffusion avec des réglages simples et calmes.",
    ],
  },
  featureCards: {
    privateJournals: {
      title: "Journaux privés",
      description:
        "Rédigez des entrées longues avec mise en forme, indiquez qui peut les recevoir plus tard et gardez vos brouillons privés jusqu'à la diffusion.",
      open: "Journal ouvert ci-dessous — cliquez à nouveau pour fermer",
      closed: "Cliquez pour ouvrir le journal →",
    },
    familyConfessions: {
      title: "Messages familiaux confidentiels",
      description:
        "Enregistrez des messages ou confidences destinés à certains proches, diffusés seulement selon le parcours de vérification que vous choisissez.",
    },
    photoMemoryCollage: {
      title: "Collage de photos et de souvenirs",
      description:
        "Ajoutez des photos et de courtes vidéos, identifiez les membres de la famille et créez une galerie qui grandit avec votre histoire.",
      open: "Galerie ouverte ci-dessous — cliquez à nouveau pour fermer",
      closed: "Ouvrir la galerie multimédia →",
    },
    posthumousReleaseWorkflow: {
      title: "Processus de transmission posthume",
      description:
        "Voyez comment les journaux et les médias passent d'un état protégé à un état diffusé après les vérifications que vous définissez avec la famille et les conseillers.",
    },
    active: "Actif",
    previewOpen: "Panneau ouvert ci-dessous — cliquez à nouveau pour fermer",
    previewClosed: "Cliquez pour prévisualiser →",
  },
  dashboard: {
    welcome: (name) => `Bienvenue, ${name}`,
    openMediaGallery: "Ouvrir la galerie multimédia",
    placeholderTitle: "Espace de fonctionnalité",
    placeholderBody:
      "Flux structuré avec destinataires identifiés, contrôles posthumes et journaux de diffusion retraçables.",
    placeholderHelperPrefix:
      "Cet espace accueillera des outils dédiés pour cette section. Pour l'instant, utilisez",
    placeholderHelperStrong: "Journaux privés",
    placeholderHelperSuffix: "pour rédiger et enregistrer vos entrées.",
    selectHint:
      "Sélectionnez une carte ci-dessus pour ouvrir son espace. Cliquez à nouveau sur la même carte pour la fermer et raccourcir la page.",
  },
  profileHero: {
    eyebrow: "Mon compte",
    title: "Mon profil",
    body:
      "Vos renseignements personnels, votre adresse, votre profession et votre contexte de santé facultatif sont enregistrés dans votre profil AfterMe afin que tout reste à jour au même endroit.",
    back: "Retour au tableau de bord",
    asideAria: "Aperçu du profil",
    asideKicker: "Profil",
    asideTitle: "Sections guidées",
    asideBody:
      "Parcourez les renseignements personnels, les coordonnées, le contexte d'identité, les notes de santé et les réglages de diffusion sans un long défilement.",
  },
  paymentHero: {
    eyebrow: "Mon compte",
    title: "Historique des paiements",
    body:
      "Consultez les paiements et frais récents enregistrés pour votre compte. Pour les renseignements du profil, utilisez Mon profil dans le même menu.",
    back: "Retour au tableau de bord",
    asideAria: "Aperçu de la facturation",
    asideKicker: "Facturation",
    asideTitle: "Registre en lecture seule",
    asideBody:
      "Les entrées apparaissent ici lorsque votre fournisseur de paiement ou nos tâches internes inscrivent des événements de facturation sur votre compte.",
  },
  adminHero: {
    eyebrow: "Opérations propriétaire",
    title: "Utilisateurs, prospects et suivi interne.",
    body:
      "Il s'agit d'un espace d'opérations protégé pour votre équipe. Il utilise la même session, tout en gardant le travail interne séparé de l'expérience client.",
    back: "Retour au tableau de bord",
    asideAria: "Résumé des opérations",
    asideKicker: "Opérations du jour",
    asideTitle: "Une seule surface interne pour le suivi produit.",
    asidePoints: [
      "Revoir les utilisateurs inscrits et les signaux de compte",
      "Suivre les nouvelles demandes de contact et les notes de suivi",
      "Préparer les futures files de vérification dans la même console",
    ],
  },
  familyHero: {
    eyebrow: "Personnes de confiance",
    title: "Vos personnes de confiance",
    body:
      "Ajoutez les personnes qui comptent dans votre plan. Vous pourrez décider plus tard qui voit quoi et à quel moment.",
    bannerStrong: "Rien n'est partagé automatiquement.",
    bannerTail: "Vous gardez le contrôle.",
    back: "Retour au tableau de bord",
    asideAria: "Aperçu de l'arbre familial",
    asideKicker: "Configuration de confiance",
    asideTitle: "Commencez par les personnes les plus proches de votre plan futur.",
    asidePoints: [
      "Ajoutez d'abord un partenaire, un enfant, un parent ou un frère ou une sœur",
      "N'ajoutez les coordonnées que si vous voulez plus tard impliquer l'accès",
      "Prévisualisez la structure avant que les parcours de diffusion n'entrent en jeu",
    ],
  },
  familyChart: {
    label: "Aperçu de votre structure familiale",
    title: "Voyez comment vos personnes de confiance se relient",
    lead:
      "Touchez une personne pour voir ses détails, survolez pour suivre ses liens et déplacez les cartes pour ordonner la vue sans changer l'ordre familial.",
    summaryAria: "Conseils pour la carte familiale",
    summaryParents: "Parents au-dessus, enfants en dessous",
    summaryTap: "Touchez une carte pour les détails",
    summaryDrag: "Déplacez pour ordonner la vue",
    svgAria: "Arbre d'accès familial",
    focusedEyebrow: "Membre sélectionné",
    ownerFocus:
      "Vous êtes le point d'ancrage du plan. Les parents sont au-dessus, votre génération reste à vos côtés et les générations plus jeunes descendent à mesure que votre plan de partage s'élargit.",
    memberFocus:
      "{name} est affiché dans la partie {relation} de votre plan afin que vous puissiez voir sa place avant de partager quoi que ce soit.",
    accountHolder: "Titulaire du compte",
    alreadyUsing: "Utilise déjà AfterMe",
    inviteRequested: "Invitation demandée",
    accessReady: "Accès prêt",
    noEmailYet: "Pas encore d'adresse courriel",
    centerOfPlan: "Centre de votre plan",
    aboveYou: "Au-dessus de vous dans la ligne familiale",
    sameGeneration: "Même génération que vous",
    belowYou: "Au-dessous de vous dans la ligne familiale",
    belowParent: (name) => `Sous ${name}`,
    besideYou: "Affiché à vos côtés",
    besidePartner: "Affiché à côté de son partenaire",
    branchUnder: (name) => `Branche sous ${name}`,
    familyMember: "Membre de la famille dans votre plan",
    otherParent: (name) => `Autre parent : ${name}`,
  },
  mediaHero: {
    kicker: "Coffre à souvenirs",
    title: "Vos souvenirs, conservés en sécurité",
    lead:
      "Conservez vos photos, documents et moments importants dans un espace sûr. Vous décidez qui les voit et quand.",
    badgesAria: "Repères de confiance du coffre à souvenirs",
    badgeEncrypted: "🔒 Chiffré et privé",
    badgeRelease: "⏳ Contrôles de diffusion disponibles",
    badgeTrusted: "👨‍👩‍👧 Partage avec la famille de confiance",
    addMemory: "Ajouter un souvenir",
    openGallery: "Ouvrir la galerie",
    back: "Retour au tableau de bord",
    loading: "Chargement des détails du coffre…",
    usageKicker: "Confiance de stockage",
    usagePlenty: "Vous avez beaucoup d'espace pour vos souvenirs.",
    usageWarning:
      "Votre coffre est presque plein. Retirez des éléments plus anciens ou mettez à niveau lorsque la facturation sera disponible.",
    usageReview:
      "Vous utilisez une plus grande part de votre espace. Passez en revue ce qui reste dans votre coffre et ce qui peut être retiré.",
    viewStorageDetails: "Voir les détails de stockage",
    memories: "Souvenirs",
    spaceUsed: "Espace utilisé",
    planSpace: "Espace de votre forfait",
    spaceLeft: "Espace restant",
  },
  verificationHero: {
    eyebrow: "Diffusion et confiance",
    title: "Vérification posthume",
    body:
      "Examinez comment les confidences et journaux privés peuvent être diffusés seulement après des vérifications approuvées. Les options ici décrivent votre parcours voulu; l'activation finale se fait lorsque votre compte entre en diffusion.",
    back: "Retour au tableau de bord",
    asideAria: "Résumé du flux de vérification",
    asideKicker: "Parcours",
    asideTitle: "Des vérifications discrètes avant toute diffusion sensible.",
    asidePoints: [
      "Choisissez le parcours de preuve que devront suivre les personnes de confiance",
      "Définissez le ton de la diffusion avant que journaux ou confidences ne bougent",
      "Gardez les étapes d'approbation séparées de l'accès quotidien au compte",
    ],
  },
  verificationWorkflow: {
    heading: "Processus de vérification posthume",
    lead: "Les confidences et journaux privés ne sont diffusés qu'après des vérifications approuvées.",
    offlineStatus: "Statut de validation hors ligne",
    manualReviewRequired: "Révision manuelle requise",
  },
  verificationOptions: [
    {
      id: "identity",
      title: "Validation de l'identité nationale",
      description:
        "Le mandataire autorisé soumet des renseignements d'identité et une confirmation signée pour une validation hors ligne.",
    },
    {
      id: "family-proof",
      title: "Soumission de preuve familiale",
      description:
        "Un membre de la famille identifié soumet le certificat de décès et la preuve du lien pour approbation manuelle.",
    },
    {
      id: "inactive-check",
      title: "Inactivité + vérification hors ligne",
      description:
        "L'inactivité du compte déclenche un parcours minuté, puis l'équipe valide les preuves avant la diffusion.",
    },
  ],
  paymentHistory: {
    title: "Historique des paiements",
    body:
      "Paiements et frais récents liés à votre compte. Cette liste se met à jour lorsque des événements de facturation sont enregistrés.",
    refresh: "Actualiser",
    refreshing: "Actualisation…",
    loading: "Chargement de l'historique des paiements…",
    fallbackPayment: "Paiement",
    emptyState:
      "Aucun paiement enregistré pour le moment. Lorsqu'un abonnement ou un paiement sera ajouté, les entrées apparaîtront ici.",
    notYet: "Pas encore",
    loadError: "Impossible de charger l'historique des paiements.",
  },
  modals: {
    changePassword: {
      title: "Changer le mot de passe",
      lead: "Entrez votre mot de passe actuel et un nouveau mot de passe (au moins 8 caractères).",
      currentPassword: "Mot de passe actuel",
      newPassword: "Nouveau mot de passe",
      confirmNewPassword: "Confirmer le nouveau mot de passe",
      fillAllFields: "Remplissez tous les champs.",
      minLength: "Le nouveau mot de passe doit contenir au moins 8 caractères.",
      mismatch: "Le nouveau mot de passe et sa confirmation ne correspondent pas.",
      updateFailed: "Impossible de mettre à jour le mot de passe.",
      updated: "Mot de passe mis à jour.",
      saving: "Enregistrement…",
      save: "Enregistrer le nouveau mot de passe",
      cancel: "Annuler",
    },
    sessionEnding: {
      title: "La session se termine bientôt",
      leadPrefix:
        "Pour votre sécurité, votre session dure 15 minutes. Lorsqu'elle se termine, vous êtes automatiquement déconnecté.",
      timeRemaining: "Temps restant : environ",
      oneMinute: "1 minute",
      manyMinutes: (count) => `${count} minutes`,
      secondLine: "Prolongez votre session pour continuer, ou déconnectez-vous maintenant.",
      extending: "Prolongation…",
      extend: "Prolonger la session",
      signOut: "Se déconnecter",
    },
    familyDelete: {
      title: "Retirer ce membre de la famille ?",
      lead: (name) =>
        `Retirez ${name} de votre arbre. Tous les médias associés à cette personne perdront cette étiquette, et les dossiers qui l'utilisaient comme destinataire par défaut perdront aussi ce réglage.`,
      removing: "Suppression…",
      remove: "Retirer le membre",
      cancel: "Annuler",
    },
  },
  heroRotator: {
    regionLabel: "Mises en avant du produit",
    dotsLabel: "Diapositives mises en avant",
    slideLabel: (index, label) => `Afficher la diapositive ${index} : ${label}`,
    slides: [
      {
        key: "journals",
        label: "Lettres et journaux",
        title: "Laissez des mots auxquels votre famille pourra revenir",
        body:
          "Rédigez les lettres, le contexte et les notes pratiques dont vos proches pourraient avoir besoin lorsque les émotions sont fortes et que les réponses manquent.",
        variant: "mint",
      },
      {
        key: "media",
        label: "Photos et souvenirs",
        title: "Gardez les souvenirs précieux privés jusqu'à la diffusion",
        body:
          "Conservez photos et courts extraits avec des destinataires identifiés pour que les moments significatifs atteignent les bonnes personnes au bon moment.",
        variant: "ocean",
      },
      {
        key: "family",
        label: "Accès familial",
        title: "Cartographiez qui devrait recevoir quoi",
        body:
          "Construisez un parcours d'accès familial qui rend vos intentions visibles avant que quelqu'un doive les interpréter plus tard.",
        variant: "violet",
      },
      {
        key: "trust",
        label: "Pensé pour la confiance",
        title: "Une diffusion conçue autour des vérifications et de la dignité",
        body:
          "Des flux structurés, des étapes de diffusion attentives et une voix produit canadienne calme pour un moment profondément sensible.",
        variant: "ember",
      },
    ],
  },
  trustedPeople: {
    relationshipOptions: [
      "Parent",
      "Conjoint",
      "Partenaire",
      "Ex-conjoint",
      "Frère ou sœur",
      "Enfant",
      "Petit-enfant",
      "Ami",
      "Conseiller juridique",
    ],
    ownerRelation: "Responsable",
    statusYou: "Vous",
    statusConnected: "Connecté",
    statusReadyToConnect: "Prêt à se connecter",
    statusInviteSent: "Invitation envoyée",
    statusInviteCancelled: "Invitation annulée",
    statusNotInvited: "Pas invité",
    badgePlanOwner: "Responsable du plan",
    badgeNoAccessYet: "Aucun accès pour l'instant",
    badgePrimaryTrusted: "Confiance principale",
    badgeFamily: "Famille",
    supportOwner: "Vous gardez le contrôle de qui recevra quoi plus tard.",
    supportConnected: "Connecté et prêt pour de futurs choix d'accès.",
    supportExistingUser:
      "Cette personne a déjà un compte AfterMe. Envoyez une invitation pour relier les deux arbres familiaux.",
    supportInviteRequested:
      "L'invitation attend encore son acceptation. Vous pouvez la renvoyer plus tard si un rappel est utile.",
    supportInviteCancelled:
      "L'invitation a été annulée. Vous pouvez en envoyer une nouvelle lorsque vous serez prêt.",
    supportEmailReady: "L'adresse courriel est prête si vous souhaitez l'impliquer plus tard.",
    supportNoAccess: "Ajouté à votre plan, mais pas encore configuré pour l'accès.",
    relationshipHints: {
      Parent: "Affiché au-dessus de vous dans la structure familiale.",
      Spouse: "Affiché à vos côtés ou à côté du partenaire choisi.",
      Partner: "Affiché à vos côtés ou à côté du partenaire choisi.",
      Child: "Affiché sous vous dans la ligne familiale.",
      Grandchild: "Placés sous l'un de vos enfants.",
      Sibling: "Affiché dans votre génération.",
      default: "Vous pourrez ajuster les choix d'accès plus tard.",
    },
    updatedPrefix: "Mis à jour",
    addedRecently: "Ajouté récemment",
    firstSent: "Premier envoi",
    lastSent: "Dernier envoi",
    lastResent: "Dernier renvoi",
    resendAvailable: "Renvoi disponible",
    resendRule: "Les invitations peuvent être renvoyées tous les 3 jours.",
    noEmailYet: "Pas encore de courriel",
    lastInviteSent: "Dernière invitation envoyée",
    inviteResendAvailable: "Renvoi disponible",
    invitationCancelled: "Invitation annulée",
    saving: "Enregistrement…",
    saveContact: "Enregistrer le contact",
    edit: "Modifier",
    cancel: "Annuler",
    sending: "Envoi…",
    sendInvite: "Envoyer l'invitation",
    sendInviteAgain: "Renvoyer l'invitation",
    resending: "Renvoi…",
    resendInvite: "Renvoyer l'invitation",
    cancelling: "Annulation…",
    cancelInvitation: "Annuler l'invitation",
    remove: "Retirer",
    pendingInvitationEyebrow: "Invitation familiale en attente",
    pendingInvitationBody:
      "{ownerName} vous a ajouté comme {inviteeName} dans son plan familial. Acceptez pour relier les deux comptes dans l'arbre familial.",
    sentPrefix: "Envoyée",
    acceptInvitation: "Accepter l'invitation",
    connecting: "Connexion…",
    guidanceAria: "Comment commencer",
    guidanceKicker: "Commencez simplement",
    guidanceTitle: "Ajoutez d'abord les personnes en qui vous avez le plus confiance.",
    guidanceBody:
      "Ajoutez maintenant votre partenaire, un parent, un enfant ou une autre personne proche. Vous pourrez toujours revenir plus tard pour élargir la structure familiale lorsque le reste de votre plan sera prêt.",
    guidancePoints: [
      "Ajoutez d'abord votre partenaire ou le membre de la famille le plus proche",
      "Réglez l'accès futur seulement lorsque vous vous sentirez prêt",
      "Cela vous aide à décider qui recevra quoi plus tard",
    ],
    panelTitle: "Créez votre cercle de confiance",
    panelBody:
      "Ajoutez les personnes qui comptent dans votre plan. Vous pourrez décider plus tard qui voit quoi et à quel moment.",
    panelBannerStrong: "Rien n'est partagé automatiquement.",
    panelBannerTail: "Vous gardez le contrôle.",
    invitationsHead: "Invitations qui vous attendent",
    invitationsBody: "Acceptez une connexion pour apparaître dans l'arbre familial l'un de l'autre.",
    stepName: "1. Nom",
    stepRelationship: "2. Relation",
    stepAccess: "3. Accès",
    stepDetails: "4. Plus de détails",
    step1: "Étape 1",
    step1Title: "Qui devrait faire partie de votre plan?",
    name: "Nom",
    fullName: "Nom complet",
    next: "Suivant",
    continueLater: "Continuer plus tard",
    step2: "Étape 2",
    step2Title: "Quelle est sa place dans votre famille?",
    relationship: "Relation",
    withParent: "Avec le parent",
    optional: "(facultatif)",
    chooseSomeone: "Choisissez quelqu'un déjà sur votre liste",
    branchUnder: "Brancher sous",
    chooseChildBranch: "Choisissez la branche d'enfant à laquelle cette personne appartient",
    addChildFirst: "Ajoutez d'abord un enfant",
    showBeside: "Afficher à côté de",
    besideYou: "Vous (titulaire du compte)",
    back: "Retour",
    step3: "Étape 3",
    step3Title: "Comment cette personne pourrait-elle être impliquée plus tard?",
    mayReceiveFuture: "Cette personne pourrait recevoir quelque chose plus tard",
    email: "Courriel",
    emailPlaceholder: "Ajoutez l'adresse qu'elle utilise ou utilisera pour AfterMe",
    emailHelp:
      "Le courriel est requis afin que nous puissions relier les deux côtés de l'arbre familial lorsqu'elle acceptera votre invitation plus tard.",
    hideDetails: "Masquer les détails",
    addMoreDetails: "Ajouter plus de détails",
    optionalSection: "Facultatif",
    optionalTitle: "Ajouter plus de détails",
    birthYear: "Année de naissance",
    birthYearPlaceholder: (year) => `p. ex. ${year}`,
    nickname: "Surnom",
    nicknamePlaceholder: "Comment vous l'appelez",
    pronouns: "Pronoms",
    pronounsPlaceholder: "Choisissez si vous le souhaitez",
    pronounsOptions: ["Il / lui", "Elle / elle", "Iel / iel", "Utilise seulement son nom", "Autre"],
    personalContext: "Contexte personnel",
    personalContextPlaceholder: "Tout ce que vous souhaitez voir retenu avec respect",
    notes: "Notes",
    notesPlaceholder:
      "Tout ce qui compte pour votre plan, le contexte familial ou de futures conversations.",
    addPerson: "Ajouter la personne",
    trialUnlockEyebrow: "Déverrouillage de l'essai",
    trialUnlockFallback: "Commencez l'essai pour ajouter plus de personnes de confiance",
    startTrial: "Commencer l'essai",
    seePlans: "Voir les forfaits",
    sidebarAria: "Personnes déjà dans votre plan",
    sidebarTitle: "Personnes déjà dans votre plan",
    sidebarBody: "Gardez cela léger pour l'instant. Vous pourrez agrandir le cercle lorsque vous serez prêt.",
    emptyTitle: "Ajoutez votre première personne de confiance",
    emptyBody:
      "Commencez par le partenaire, le parent, l'enfant ou le frère ou la sœur autour de qui vous risquez le plus de planifier.",
    addSomeone: "Ajouter quelqu'un",
    privacyAria: "Vie privée et contrôle",
    privacyKicker: "Votre vie privée compte",
    privacyTitle: "Vous gardez le contrôle à chaque étape.",
    privacyPoints: [
      "Vous seul décidez qui recevra un accès plus tard.",
      "Rien n'est partagé automatiquement simplement parce qu'une personne est inscrite ici.",
      "Vous pouvez modifier les relations, courriels et rôles à tout moment.",
    ],
  },
  familyInvitationNotice: {
    waitingOne: "Vous avez 1 invitation familiale en attente dans l'arbre familial.",
    waitingMany: (count) => `Vous avez ${count} invitations familiales en attente dans l'arbre familial.`,
    connectedWith: (ownerName) =>
      `Vous êtes maintenant connecté avec ${ownerName || "votre contact familial"} dans l'arbre familial.`,
  },
};

export function getAppCopy(locale = "en") {
  return locale === "fr" ? FR_APP_COPY : EN_APP_COPY;
}
